import type { Endpoint } from "comlink";

declare global {
  interface Window {
    uxpHost?: { postMessage(msg: unknown): void };
  }
}

/**
 * Creates a Comlink-compatible endpoint for the UXP shell side.
 * Used with `Comlink.expose(host, shellEndpoint)` in the UXP panel.
 */
export function createShellEndpoint(webview: HTMLElement): Endpoint {
  return {
    postMessage: (msg: unknown) => {
      (webview as unknown as { postMessage(msg: unknown): void }).postMessage(
        msg,
      );
    },
    addEventListener: (
      _type: string,
      listener: EventListenerOrEventListenerObject,
    ) => {
      webview.addEventListener("message", listener);
    },
    removeEventListener: (
      _type: string,
      listener: EventListenerOrEventListenerObject,
    ) => {
      webview.removeEventListener("message", listener);
    },
  };
}

/**
 * Creates a Comlink-compatible endpoint for the webview side.
 * Used with `Comlink.wrap<IAdobeHost>(webviewEndpoint)` in the panel UI.
 *
 * Requires `window.uxpHost` to be available (UXP webview runtime).
 */
export function createWebviewEndpoint(): Endpoint {
  if (!window.uxpHost) {
    throw new Error(
      "window.uxpHost is not available — not running in a UXP webview",
    );
  }

  return {
    postMessage: (msg: unknown) => {
      window.uxpHost?.postMessage(msg);
    },
    addEventListener: (
      _type: string,
      listener: EventListenerOrEventListenerObject,
    ) => {
      window.addEventListener("message", listener);
    },
    removeEventListener: (
      _type: string,
      listener: EventListenerOrEventListenerObject,
    ) => {
      window.removeEventListener("message", listener);
    },
  };
}
